import React, { useEffect, useState, useRef } from 'react';
import { ChromePicker } from 'react-color';
import { 
  Box, Button, Heading, Text, Divider, SimpleGrid, Flex, useToast, 
  Card, CardBody, VStack, Badge, HStack, Container, IconButton, 
  Tooltip, useColorModeValue
} from '@chakra-ui/react';
import { EditIcon, InfoIcon, WarningIcon } from '@chakra-ui/icons';
import { FiRefreshCw } from 'react-icons/fi';

// Define the API URL (Ensure you set this in your environment)
const API_BASE_URL =
  process.env.NODE_ENV === 'production'
    ? process.env.REACT_APP_VERCEL_API_URL
    : process.env.REACT_APP_LOCAL_API_URL;

// Helper function to convert '0xFFF5EEDC' to '#F5EEDC'
const convertToHex = (colorValue) => {
  if (!colorValue || typeof colorValue !== 'string') {
    return '#FFFFFF'; // Return white if colorValue is not valid
  }
  
  if (colorValue.startsWith('0xFF')) {
    return `#${colorValue.slice(4)}`; // Convert from '0xFF...' to '#...'
  } else if (colorValue.startsWith('#')) {
    return colorValue; // Already in '#...' format, return as is
  }
  return '#FFFFFF'; // Fallback to white
};

// Helper function to convert '#F5EEDC' to '0xFFF5EEDC'
const convertTo0xFormat = (hexColor) => {
  return `0xFF${hexColor.slice(1).toUpperCase()}`; // Remove '#' and prepend '0xFF'
};

const ThemeColorPage = () => {
  // Theme colors from CategoryList
  const themeColor = "#4bedffff";
  const themeTextColor = "black";
  const bgMain = useColorModeValue('gray.50', 'gray.800');
  const bgCard = useColorModeValue('white', 'gray.700');
  const bgInput = useColorModeValue('white', 'gray.800');

  // Default color values (these will be rendered regardless of fetch status)
  const defaultColors = {
    backgroundColor: '0xFFBDFDF6',
    primaryColor: '0xFFFFD600',
    secondaryColor: '0xFFD7CCC8',
    textColor: '0xFF3E2723',
    searchBarColor: '0xFFF2D7B5',
    primaryVariantColor: '0xFF087F4A',
    surfaceColor: '0xFFFFFFFF',
    errorColor: '0xFFD32F2F',
    onPrimaryColor: '0xFFFFFFFF',
    onSecondaryColor: '0xFFFFFFFF',
    onBackgroundColor: '0xFF000000',
    onSurfaceColor: '0xFF000000',
    onErrorColor: '0xFFFFFFFF',
  };

  // State to hold the colors
  const [colors, setColors] = useState(defaultColors); // Initial state is set to defaultColors
  const [updatedColors, setUpdatedColors] = useState({}); // To track changes made by the user
  const [loading, setLoading] = useState(false);
  const toast = useToast();

  // Fetch the color configuration from the backend
  const hasFetched = useRef(false);
  useEffect(() => {
    if (hasFetched.current) return;
    hasFetched.current = true;
    setLoading(true);
    fetch(`${API_BASE_URL}api/colors`)
      .then((response) => response.json())
      .then((data) => {
        // Check if data has the necessary color properties (e.g., backgroundColor)
        if (data && Object.keys(data).length > 0) {
          setColors(data); // Set the colors from the backend directly
        } else {
          setColors(defaultColors); // If no colors are found, use default colors
        }
      })
      .catch((error) => {
        console.error('Error fetching colors:', error);
        setColors(defaultColors); // Use default colors in case of an error
        toast({
          title: 'Erro',
          description: 'Falha ao carregar configurações de cores',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  const handleColorChange = (field, color) => {
    setUpdatedColors((prevState) => ({
      ...prevState,
      [field]: color.hex,
    }));
  };

  // Handle saving changes to the backend
  const handleSubmitThemeChange = async (field) => {
    setLoading(true);
    const token = localStorage.getItem('userToken_TOVIAPPS'); // Assuming the token is stored in localStorage
    try {
      // Convert the color back to '0xFF...' format before saving
      const updatedFieldColor = {
        ...colors,
        [field]: convertTo0xFormat(updatedColors[field] || convertToHex(colors[field])),
      };
  
      const response = await fetch(`${API_BASE_URL}api/colors`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`, // Include the token in the Authorization header
        },
        body: JSON.stringify(updatedFieldColor),
      });
  
      if (!response.ok) {
        throw new Error(`Erro ao atualizar ${field}`);
      }
  
      setColors(updatedFieldColor); // Update the local state with the new colors
      toast({
        title: 'Sucesso!',
        description: `A cor de ${field} foi atualizada com sucesso!`,
        status: 'success',
        duration: 5000,
        isClosable: true,
        position: 'top-right',
      });
    } catch (error) {
      console.error(`Error updating ${field}:`, error);
      toast({
        title: 'Erro!',
        description: `Ocorreu um erro ao atualizar ${field}. Tente novamente.`,
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top-right',
      });
    } finally {
      setLoading(false);
    }
  };
  
  const handleResetColorsToDefault = async () => {
    setLoading(true);
    const token = localStorage.getItem('userToken_TOVIAPPS');
    try {
      const response = await fetch(`${API_BASE_URL}api/colors/reset-default`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
      });
  
      if (!response.ok) {
        throw new Error('Erro ao redefinir as cores para os padrões');
      }
  
      toast({
        title: 'Sucesso!',
        description: 'As cores foram redefinidas para os padrões com sucesso!',
        status: 'success',
        duration: 5000,
        isClosable: true,
        position: 'top-right',
      });
  
      // Reload the page after showing the success message
      setTimeout(() => {
        window.location.reload();
      }, 2000);
    } catch (error) {
      console.error('Error resetting colors:', error);
      toast({
        title: 'Erro!',
        description: 'Ocorreu um erro ao redefinir as cores. Tente novamente.',
        status: 'error',
        duration: 5000,
        isClosable: true,
        position: 'top-right',
      });
    } finally {
      setLoading(false);
    }
  };
  
  // Function to refresh color data
  const refreshColors = () => {
    setLoading(true);
    fetch(`${API_BASE_URL}api/colors`)
      .then((response) => response.json())
      .then((data) => {
        if (data && Object.keys(data).length > 0) {
          setColors(data);
          setUpdatedColors({});
          toast({
            title: 'Sucesso',
            description: 'Cores atualizadas com sucesso',
            status: 'success',
            duration: 5000,
            isClosable: true,
          });
        }
      })
      .catch((error) => {
        console.error('Error refreshing colors:', error);
        toast({
          title: 'Erro',
          description: 'Falha ao atualizar cores',
          status: 'error',
          duration: 5000,
          isClosable: true,
        });
      })
      .finally(() => {
        setLoading(false);
      });
  };

  return (
    <Container maxW="container.xl" p={6}>
      {/* Header Card */}
      <Card mb={6} shadow="md" borderRadius="lg" overflow="hidden" bg="#fff8e1">
        <CardBody>
          <Flex justify="space-between" align="center" wrap="wrap">
            <HStack>
              <Heading as="h1" fontSize="2xl" fontWeight="bold" color="black">
                Editor de Cores do Tema
              </Heading>
              <Badge bg={themeColor} color={themeTextColor} borderRadius="full" px={3} py={1}>
                {Object.keys(colors).length} cores
              </Badge>
            </HStack>
            
            <HStack spacing={3}>
              <Button
                leftIcon={<FiRefreshCw />}
                size="sm"
                variant="outline"
                borderColor={themeColor}
                color="black"
                onClick={refreshColors}
                isLoading={loading}
              >
                Atualizar
              </Button>
              
              <Button
                bg={themeColor}
                color={themeTextColor}
                onClick={handleResetColorsToDefault}
                isLoading={loading}
                _hover={{ bg: "#e5c100" }}
              >
                Restaurar Cores Padrão
              </Button>
            </HStack>
          </Flex>
        </CardBody>
      </Card>

      {/* Mobile App Preview and Color Pickers */}
      <SimpleGrid columns={{ base: 1, lg: 4 }} spacing={8}>
        {/* Mobile Preview Card */}
        <Card shadow="md" borderRadius="lg" mb={{ base: 6, lg: 0 }} bg="#fff8e1">
          <CardBody>
            <VStack spacing={4} align="stretch">
              <Text fontWeight="bold" color="black">Pré-visualização do App</Text>
              <MobileAppPreview colors={{ ...colors, ...updatedColors }} />
            </VStack>
          </CardBody>
        </Card>

        {/* Color Pickers */}
        <SimpleGrid columns={{ base: 1, md: 3, lg: 3 }} spacing={6} gridColumn={{ lg: "span 3" }}>
          {Object.keys(colors).map((colorKey) => (
            <ColorPickerCard
              key={colorKey}
              colorName={colorKey}
              currentColor={updatedColors[colorKey] || convertToHex(colors[colorKey])}
              onChange={(color) => handleColorChange(colorKey, color)}
              onSave={() => handleSubmitThemeChange(colorKey)}
              isLoading={loading}
              themeColor={themeColor}
              themeTextColor={themeTextColor}
            />
          ))}
        </SimpleGrid>
      </SimpleGrid>

      {/* Default Colors Section */}
      <Card mt={8} shadow="md" borderRadius="lg" bg="#fff8e1">
        <CardBody>
          <Heading size="md" mb={4} color="black">Cores Padrão do Aplicativo</Heading>
          <SimpleGrid columns={{ base: 2, md: 3, lg: 4 }} spacing={6}>
            {Object.keys(defaultColors).map((colorKey) => {
            const colorValue = defaultColors[colorKey];
            const hexColor = typeof colorValue === 'string' ? convertToHex(colorValue) : '#FFFFFF';
            
            return (
            <Flex key={colorKey} alignItems="center">
            <Box
              w="40px"
              h="40px"
              borderRadius="md"
              bg={hexColor}
                border="1px solid"
                borderColor="gray.200"
              mr={3}
              boxShadow="sm"
            />
            <VStack align="start" spacing={0}>
                <Text fontWeight="medium" color="black">{colorKey}</Text>
                  <Text fontSize="xs" color="gray.500">
                      {hexColor}
                </Text>
              </VStack>
            </Flex>
          );
        })}
          </SimpleGrid>
        </CardBody>
      </Card>
    </Container>
  );
};

// Component for individual color picker cards
const ColorPickerCard = ({ colorName, currentColor, onChange, onSave, isLoading, themeColor, themeTextColor }) => {
  // Ensure currentColor is a valid color string
  const safeColor = typeof currentColor === 'string' ? currentColor : '#FFFFFF';
  
  return (
    <Card shadow="sm" borderRadius="lg" overflow="hidden" bg="#fff8e1">
      <Box bg={themeColor} h="8px" w="100%" />
      <CardBody p={4}>
        <VStack spacing={3} align="stretch">
          <Flex justify="space-between" align="center">
            <Text fontWeight="bold" noOfLines={1} color="black">{colorName}</Text>
            <Box
              w="30px"
              h="30px"
              borderRadius="md"
              bg={safeColor}
              border="1px solid"
              borderColor="gray.200"
              boxShadow="sm"
            />
          </Flex>
          
          <Box>
            <ChromePicker
              color={safeColor}
              onChange={onChange}
              disableAlpha={true}
              styles={{
                default: {
                  picker: {
                    width: '100%',
                    boxShadow: 'none',
                    borderRadius: '0.5rem',
                  }
                }
              }}
            />
          </Box>
          
          <Button
            bg={themeColor}
            color={themeTextColor}
            onClick={onSave}
            isLoading={isLoading}
            size="sm"
            width="100%"
            _hover={{ bg: "#e5c100" }}
            leftIcon={<EditIcon />}
          >
            Salvar
          </Button>
        </VStack>
      </CardBody>
    </Card>
  );
};

// Component to display a live mobile app preview
const MobileAppPreview = ({ colors }) => {
  // Ensure colors is a valid object
  const safeColors = colors && typeof colors === 'object' ? colors : {};
  return (
    <Box 
      display="flex"
      justifyContent="center"
      alignItems="center"
      py={4}
    >
      <Box 
        w="240px"
        h="480px"
        border="10px solid #000"
        borderRadius="30px"
        bg={safeColors.backgroundColor ? convertToHex(safeColors.backgroundColor) : '#F5F5F5'}
        display="flex"
        flexDirection="column"
        position="relative"
        overflow="hidden"
        boxShadow="xl"
      >
        {/* Camera & Speaker Section */}
        <Flex justifyContent="center" alignItems="center" mt={2} mb={1}>
          <Box 
            w="60px" 
            h="4px" 
            bg="#333" 
            borderRadius="md" 
          />
        </Flex>

        {/* Navbar / Header */}
        <Box 
          p={3} 
          bg={safeColors.primaryColor ? convertToHex(safeColors.primaryColor) : '#795548'} 
          color={safeColors.onPrimaryColor ? convertToHex(safeColors.onPrimaryColor) : '#FFFFFF'}
          textAlign="center" 
        >
          <Text fontSize="sm" fontWeight="bold">Visualização do App</Text>
        </Box>

        {/* Main Content Section */}
        <Box 
          flex="1" 
          p={3} 
          bg={safeColors.backgroundColor ? convertToHex(safeColors.backgroundColor) : '#FFFFFF'} 
          display="flex"
          flexDirection="column"
          justifyContent="space-between"
          overflowY="auto"
        >
          {/* Search Bar */}
          <Box 
            mb={3} 
            p={2} 
            bg={safeColors.searchBarColor ? convertToHex(safeColors.searchBarColor) : '#F2D7B5'} 
            borderRadius="md"
            fontSize="xs"
            color={safeColors.textColor ? convertToHex(safeColors.textColor) : '#3E2723'}
          >
            🔍 Pesquisar...
          </Box>
          
          {/* Content Cards */}
          <VStack spacing={3} align="stretch" mb={3}>
            <Box 
              p={2} 
              bg={safeColors.surfaceColor ? convertToHex(safeColors.surfaceColor) : '#FFFFFF'} 
              borderRadius="md" 
              borderLeft="4px solid"
              borderColor={safeColors.primaryColor ? convertToHex(safeColors.primaryColor) : '#795548'}
              boxShadow="sm"
            >
              <Text 
                fontSize="xs" 
                fontWeight="bold"
                color={safeColors.textColor ? convertToHex(safeColors.textColor) : '#3E2723'}
              >
                Exemplo de Card
              </Text>
              <Text 
                fontSize="2xs" 
                color={safeColors.textColor ? convertToHex(safeColors.textColor) : '#3E2723'}
              >
                Descrição do conteúdo aqui
              </Text>
            </Box>
            
            <Box 
              p={2} 
              bg={safeColors.secondaryColor ? convertToHex(safeColors.secondaryColor) : '#D7CCC8'} 
              borderRadius="md" 
              boxShadow="sm"
            >
              <Text 
                fontSize="xs" 
                color={safeColors.textColor ? convertToHex(safeColors.textColor) : '#3E2723'}
              >
                Card Secundário
              </Text>
            </Box>
            
            <Box 
              p={2} 
              bg={safeColors.errorColor ? convertToHex(safeColors.errorColor) : '#D32F2F'} 
              borderRadius="md" 
              boxShadow="sm"
            >
              <Text 
                fontSize="xs" 
                color={safeColors.onErrorColor ? convertToHex(safeColors.onErrorColor) : '#FFFFFF'}
              >
                Mensagem de Erro
              </Text>
            </Box>
          </VStack>

          {/* Buttons */}
          <VStack spacing={2} align="stretch">
            <Button
              size="xs"
              bg={safeColors.primaryColor ? convertToHex(safeColors.primaryColor) : '#795548'}
              color={safeColors.onPrimaryColor ? convertToHex(safeColors.onPrimaryColor) : '#FFFFFF'}
              _hover={{ bg: safeColors.primaryVariantColor ? convertToHex(safeColors.primaryVariantColor) : '#087F4A' }}
            >
              Botão Primário
            </Button>
            
            <Button
              size="xs"
              bg={safeColors.secondaryColor ? convertToHex(safeColors.secondaryColor) : '#D7CCC8'}
              color={safeColors.onSecondaryColor ? convertToHex(safeColors.onSecondaryColor) : '#FFFFFF'}
              _hover={{ bg: safeColors.primaryColor ? convertToHex(safeColors.primaryColor) : '#795548' }}
            >
              Botão Secundário
            </Button>
          </VStack>
        </Box>

        {/* Bottom Navigation */}
        <Flex 
          p={2} 
          bg={safeColors.primaryColor ? convertToHex(safeColors.primaryColor) : '#795548'} 
          justifyContent="space-around"
          alignItems="center"
        >
          <Box w="16px" h="16px" bg={safeColors.onPrimaryColor ? convertToHex(safeColors.onPrimaryColor) : '#FFFFFF'} borderRadius="full" />
          <Box w="16px" h="16px" bg={safeColors.onPrimaryColor ? convertToHex(safeColors.onPrimaryColor) : '#FFFFFF'} borderRadius="full" />
          <Box w="24px" h="24px" bg={safeColors.onPrimaryColor ? convertToHex(safeColors.onPrimaryColor) : '#FFFFFF'} borderRadius="full" />
          <Box w="16px" h="16px" bg={safeColors.onPrimaryColor ? convertToHex(safeColors.onPrimaryColor) : '#FFFFFF'} borderRadius="full" />
        </Flex>
      </Box>
    </Box>
  );
};

export default ThemeColorPage;